import React, { useEffect, useState } from "react";
import { GridColDef } from "@mui/x-data-grid";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import PersonOffIcon from "@mui/icons-material/PersonOff";
import GavelIcon from "@mui/icons-material/Gavel";
import PriorityHighIcon from "@mui/icons-material/PriorityHigh";

import { FilterableDataGrid } from "../common/FilterableDataGrid";
import { ConfirmActionDialog } from "../common/ConfirmActionDialog";
import { RowActionsMenu, RowAction } from "../common/RowActionsMenu";
import {
  ModerationQueueFlag,
  ModerationReviewResource,
} from "../../api/moderation";
import {
  useModerationStore,
  ReviewStatusFilter,
} from "../../store/moderationStore";
import { useSnackbar } from "../../hooks/useSnackbar";
import { useGlobalLoading } from "../../hooks/useGlobalLoading";
import { formatAge, formatDateTime, truncateText } from "./utils";
import { SuspendUploadsDialog } from "./SuspendUploadsDialog";

/**
 * The review queue for flags a human has to decide on.
 *
 * Deliberately not the chat flags list beside it. That one is a record of what
 * Comprehend already did — nothing ever changes a chat flag's state — while
 * every row here is work. The two also read `status` in opposite directions, so
 * the vocabulary is kept apart: nothing in this view says FLAGGED or DELETED.
 */
export const ImageReviewList: React.FC = () => {
  const {
    reviewFlags,
    loadingReviewFlags,
    reviewNextToken,
    loadReviewFlags,
    reviewError,
    reviewStatus,
  } = useModerationStore();
  const { showSnackbar, SnackbarNode } = useSnackbar();
  const { withLoading } = useGlobalLoading();

  const [confirming, setConfirming] = useState<{
    flag: ModerationQueueFlag;
    verdict: "approve" | "reject";
  } | null>(null);
  const [suspending, setSuspending] = useState<ModerationQueueFlag | null>(
    null,
  );
  /** The flag a moderator is about to place under legal hold. */
  const [holding, setHolding] = useState<ModerationQueueFlag | null>(null);
  /**
   * Whether rejecting should also take the member's published picture down.
   *
   * Ticked by default, because leaving a reported picture up is rarely what a
   * moderator means by "reject" — and until this existed, rejecting a report
   * changed nothing a member could see. The default lives here rather than on
   * the server: a client that predates the field must not start removing
   * pictures because it omitted one.
   */
  const [removePicture, setRemovePicture] = useState(true);
  /**
   * The moderator's note on this decision.
   *
   * Internal: the API records it on the flag and shows it to no member, and
   * the removal email is built from the Terms section rather than from this.
   * Cleared whenever a dialog opens, so a note written about one flag cannot
   * follow the moderator onto the next.
   */
  const [reviewNotes, setReviewNotes] = useState("");

  useEffect(() => {
    // The status the store already holds, not a literal PENDING. This list is
    // unmounted whenever the moderator switches to the chat tab, while the
    // store outlives it — so fetching PENDING here would come back to a grid
    // whose status control had already been seeded REJECTED from that same
    // store, and whose client-side filter would then drop every row fetched.
    // A blank grid, no error, and no empty-state alert, because that one is
    // gated on the unfiltered row count.
    //
    // On a genuinely first mount the store reads ALL, which is the default
    // view: pending rows sort to the top of it, so the work is still what a
    // moderator lands on first.
    loadReviewFlags(reviewStatus);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Whether this flag is still open, and so still actionable.
   *
   * The server already refuses a second decision with a 409, so hiding the
   * verdict buttons on a decided row reflects a rule that exists rather than
   * inventing one. Legal hold and suspend are deliberately *not* gated on it —
   * see the action column.
   */
  const isPending = (flag: ModerationQueueFlag) => flag.status === "PENDING";

  /** Reload whatever the control is currently showing, not always PENDING. */
  const reload = () => loadReviewFlags(reviewStatus);

  /**
   * The selected status as it reads mid-sentence in the alerts below.
   *
   * Empty under "All", so the sentences read "older flags" and "No flags"
   * rather than "older all flags".
   */
  const statusWord =
    reviewStatus === "ALL" ? "" : `${reviewStatus.toLowerCase()} `;

  // The store asks for pictures and reports by kind, so chat flags never arrive
  // here — they belong to the list next door, which describes `status` the
  // opposite way round. Filtering them out client-side instead would mean a
  // page of chat could leave this view empty while pictures wait unreviewed.
  const rows = reviewFlags;

  /**
   * Whether approving this flag publishes anything.
   *
   * Only an image the automated gate refused has an object behind it. A member
   * reporting someone's picture writes the same content type but carries the
   * reported category instead, and a conduct report has no artifact at all —
   * approving either resolves the row and publishes nothing.
   */
  const publishesOnApprove = (flag: ModerationQueueFlag) =>
    flag.contentType === "PROFILE_PICTURE" && flag.source === "REKOGNITION";

  /**
   * Whether this flag is about a picture the member may have live right now.
   *
   * The mirror of `publishesOnApprove`, and it mirrors the server's own guard: a
   * picture flagged by a person passed the automated gate and went public, which
   * is the only case with anything to take down. An automated flag's image was
   * never published, and a conduct report is not about a picture at all.
   */
  const canRemoveOnReject = (flag: ModerationQueueFlag) =>
    flag.contentType === "PROFILE_PICTURE" &&
    (flag.source === "MEMBER" || flag.source === "GUEST") &&
    // A reporter who named someone in free text rather than from a roster row
    // leaves no member to act on, and the server can only take a picture down
    // by member. Offering the box here would promise a removal that cannot
    // happen and then report it as done. The suspend button below is gated on
    // the same field for the same reason.
    !!flag.senderId;

  /**
   * Whether this flag has a picture that can be placed under legal hold.
   *
   * Any picture flag, from either writer: an automated refusal names its
   * retained object, and a report names a member whose picture is live or was
   * taken down. The server decides which copy to hold and refuses with a 409
   * when there is nothing — a report naming nobody, say — so this only hides
   * the button where it could never apply.
   */
  const canHold = (flag: ModerationQueueFlag) =>
    flag.contentType === "PROFILE_PICTURE";

  const hold = async (flag: ModerationQueueFlag) => {
    try {
      const result = await withLoading(
        ModerationReviewResource.preserve(flag.id),
      );
      await reload();
      showSnackbar(
        result.alreadyHeld
          ? "Already under legal hold — nothing changed"
          : "Placed under legal hold. The member has not been told.",
        "success",
      );
    } catch (err: any) {
      // 409 means there was nothing to hold — the report names nobody, or the
      // member has no picture live or retained. The server's reason says which.
      showSnackbar(err?.message || "Failed to place the hold", "error");
      await reload();
    } finally {
      setHolding(null);
    }
  };

  const decide = async (
    flag: ModerationQueueFlag,
    verdict: "approve" | "reject",
  ) => {
    try {
      // Typed explicitly: approve's result is a subtype of reject's, so inference
      // would collapse the union to reject's shape and lose `publicUrl`.
      // Empty stays empty: an all-whitespace note would record a blank string
      // where the absence of one is the honest answer.
      const notes = reviewNotes.trim() || undefined;
      const result = await withLoading<
        Awaited<ReturnType<typeof ModerationReviewResource.approve>>
      >(
        verdict === "approve"
          ? ModerationReviewResource.approve(flag.id, notes)
          : ModerationReviewResource.reject(
              flag.id,
              notes,
              // Only ever sent where it applies, so a stale tick from a previous
              // dialog cannot reach a flag it was not meant for.
              canRemoveOnReject(flag) && removePicture,
            ),
      );
      await reload();
      showSnackbar(
        verdict === "approve"
          ? result.publicUrl
            ? "Approved — the picture is now live"
            : "Approved"
          : canRemoveOnReject(flag) && removePicture
            ? // Deliberately not "the picture was removed". The approve branch
              // above can assert its outcome because the response carries
              // `publicUrl`; a reject carries no removal signal at all, so the
              // server skipping the takedown — the member cleared it first, a
              // partial sweep that does not fail the request — would otherwise
              // read as a completed one.
              "Rejected — removal requested"
            : "Rejected — nothing was published",
        "success",
      );
    } catch (err: any) {
      // 409 is the ordinary case, not a fault: another moderator got there
      // first, or the member has published a different picture since. Say which
      // rather than showing a raw status.
      showSnackbar(err?.message || `Failed to ${verdict} the flag`, "error");
      await reload();
    } finally {
      setConfirming(null);
    }
  };

  /**
   * The resolution columns, shown only when looking at decided flags.
   *
   * On the pending queue all three are empty by definition, and three blank
   * columns on the view a moderator lands on is noise in the one place the
   * screen should be a worklist.
   */
  const resolutionColumns: GridColDef[] =
    reviewStatus === "PENDING"
      ? []
      : [
          {
            field: "status",
            headerName: "Decision",
            // Sized to the caption beneath the verdict — a formatted timestamp
            // plus a truncated moderator id — which was clipping at 185.
            width: 240,
            renderCell: (params) => {
              const flag = params.row as ModerationQueueFlag;
              if (flag.status === "PENDING") {
                return (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0.5,
                      color: "text.secondary",
                    }}
                  >
                    <PriorityHighIcon fontSize="small" />
                    <Typography
                      variant="body2"
                      color="warning.main"
                      sx={{ fontWeight: 700 }}
                    >
                      Awaiting review
                    </Typography>
                  </Box>
                );
              }
              const approved = flag.status === "APPROVED";
              const cell = (
                <Box sx={{ minWidth: 0 }}>
                  <Box
                    sx={{ display: "flex", alignItems: "center", gap: 0.75 }}
                  >
                    {/* The only saturated pixels in the grid. A verdict is the
                        one thing worth finding without reading, so it gets the
                        colour budget and nothing else competes for it. */}
                    <Box
                      component="span"
                      sx={{
                        width: 6,
                        height: 6,
                        borderRadius: "50%",
                        flexShrink: 0,
                        bgcolor: approved ? "success.main" : "error.main",
                      }}
                    />
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {approved ? "Approved" : "Rejected"}
                    </Typography>
                  </Box>
                  {/* When and by whom, under the verdict rather than in two
                      more columns of their own. */}
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: "block", lineHeight: 1.4 }}
                  >
                    {flag.reviewedAt ? formatDateTime(flag.reviewedAt) : "—"}
                    {flag.reviewedBy
                      ? ` · ${truncateText(flag.reviewedBy, 14)}`
                      : ""}
                  </Typography>
                </Box>
              );
              // Notes are plumbed through approve/reject but nothing sent one
              // until now, so most rows have none. Shown where present rather
              // than given a column that would be blank for everything.
              const title = [
                flag.reviewedBy ? `Decided by ${flag.reviewedBy}` : null,
                flag.reviewNotes,
              ]
                .filter(Boolean)
                .join(" — ");
              return title ? <Tooltip title={title}>{cell}</Tooltip> : cell;
            },
          },
        ];

  const columns: GridColDef[] = [
    {
      field: "thumbnailUrl",
      headerName: "",
      width: 56,
      sortable: false,
      renderCell: (params) => {
        const flag = params.row as ModerationQueueFlag;
        // Only an automated refusal names an object. A reported picture carries
        // the reported category in `content`, so there is nothing to show.
        if (!flag.thumbnailUrl) {
          return (
            <Tooltip title="No image — this was reported, not scanned">
              <Avatar variant="rounded" sx={{ width: 40, height: 40 }} />
            </Tooltip>
          );
        }
        return (
          <Avatar
            variant="rounded"
            src={flag.thumbnailUrl}
            alt="Flagged picture"
            sx={{ width: 40, height: 40 }}
          />
        );
      },
    },
    {
      // Age, not a timestamp. A queue is worked by how long something has been
      // waiting, and "3 days ago" answers that at a glance where
      // "2026-09-01 12:00:00" has to be subtracted from today first. The exact
      // time is a hover away, and the column is 65px narrower for it.
      field: "createdAt",
      headerName: "Flagged",
      width: 90,
      renderCell: (params) => (
        <Tooltip title={formatDateTime(params.value)}>
          <Typography variant="body2" noWrap>
            {formatAge(params.value)}
          </Typography>
        </Tooltip>
      ),
    },
    {
      // "Raised by" and "Confidence" were two columns encoding one fact: a
      // human report has no score, and said so in words that repeated the
      // source. One cell now — who raised it, with the model's score beneath
      // when there is one.
      field: "source",
      headerName: "Raised by",
      width: 110,
      renderCell: (params) => {
        const flag = params.row as ModerationQueueFlag;
        const label =
          flag.source === "REKOGNITION"
            ? "Automated"
            : flag.source === "MEMBER"
              ? "Member"
              : flag.source === "GUEST"
                ? "Guest"
                : flag.source;
        return (
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="body2">{label}</Typography>
            {flag.confidence != null && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", lineHeight: 1.4 }}
              >
                {(flag.confidence * 100).toFixed(1)}% confident
              </Typography>
            )}
          </Box>
        );
      },
    },
    {
      field: "senderId",
      headerName: "Reported member",
      // The column that takes the grid's spare width, and the one that can use
      // it: an account id truncated at a fixed 14 characters showed the same
      // little of a long id however much room there was.
      //
      // Uncapped on purpose. Every column having a ceiling leaves the grid
      // narrower than its container, and MUI fills the remainder with a filler
      // element that carries a separator and none of the row's background —
      // which reads as an empty ninth column. One column has to absorb the
      // slack, and an id is the content least harmed by extra room.
      flex: 1,
      minWidth: 160,
      renderCell: (params) =>
        params.value ? (
          <Tooltip title={params.value}>
            <Typography variant="body2" noWrap>
              {params.value}
            </Typography>
          </Tooltip>
        ) : (
          // A free-text report names its subject in prose, not as an id.
          <Tooltip title="Named in the report, not linked to an account">
            <span>—</span>
          </Tooltip>
        ),
    },
    {
      field: "categories",
      headerName: "Categories",
      // Fixed, not flex. The content is one short phrase — "Profile Picture"
      // on most rows — and as the only flexing column it was absorbing every
      // spare pixel in the grid to display it.
      width: 160,
      renderCell: (params) => {
        // Text, not chips. Every row had them, so they marked nothing — and a
        // category list is read, not scanned for.
        const categories: string[] = params.value || [];
        if (categories.length === 0) {
          return (
            <Typography variant="body2" color="text.secondary">
              —
            </Typography>
          );
        }
        const all = categories.join(", ");
        return (
          <Tooltip title={all}>
            <Typography variant="body2" noWrap>
              {all}
            </Typography>
          </Tooltip>
        );
      },
    },
    {
      // What is in force on this row, as distinct from what was decided about
      // it. A hold is not a verdict and not a punishment — it preserves
      // material, and can sit on a flag whose subject has done nothing wrong —
      // so it does not belong in Decision, and it was crowding the action
      // cluster when it lived there.
      //
      // "Restrictions" plural because an upload suspension belongs here too:
      // `ModerationQueueFlag` does not carry one yet (it lives behind
      // GET /v1/admin/moderation/uploads/{userId}/suspension, one call per
      // member), so for now this column speaks only for the hold.
      field: "preservedAt",
      headerName: "Restrictions",
      width: 105,
      renderCell: (params) => {
        const flag = params.row as ModerationQueueFlag;
        if (!flag.preservedAt || !canHold(flag)) {
          return (
            <Typography variant="body2" color="text.secondary">
              —
            </Typography>
          );
        }
        return (
          <Tooltip
            title={`Under legal hold since ${formatDateTime(flag.preservedAt)}. Only a developer can release it.`}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                color: "text.secondary",
              }}
            >
              <GavelIcon fontSize="small" />
              <Typography variant="body2">Held</Typography>
            </Box>
          </Tooltip>
        );
      },
    },
    ...resolutionColumns,
    {
      field: "actions",
      headerName: "Actions",
      width: 200,
      sortable: false,
      align: "right",
      // Matches the cells. A centred header over right-packed buttons reads as
      // a misalignment rather than a choice.
      headerAlign: "right",
      renderCell: (params) => {
        const flag = params.row as ModerationQueueFlag;

        // Hold and suspend go behind one menu on every row. They are the
        // occasional actions — a hold is for material being escalated, a
        // suspension is about the member rather than this flag — and giving
        // them permanent buttons made four controls compete on a row where at
        // most two are the point.
        const secondary: RowAction[] = [];
        if (canHold(flag) && !flag.preservedAt) {
          secondary.push({
            key: "hold",
            label: "Place under legal hold",
            icon: <GavelIcon fontSize="small" />,
            onClick: () => setHolding(flag),
          });
        }
        if (flag.senderId) {
          secondary.push({
            key: "suspend",
            label: "Bar from uploading",
            icon: <PersonOffIcon fontSize="small" />,
            onClick: () => setSuspending(flag),
          });
        }

        return (
          // Packed to the right, so the overflow menu lands in the same place
          // on every row: a decided row carries only the menu, a pending row
          // carries two verdicts and the menu, and left- or centre-packing
          // would put that one control in two different places down the
          // column.
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "end",
              gap: 0.5,
              width: "100%",
            }}
          >
            {/* Decided rows carry no verdict. The server answers 409 on a
                second decision, so offering one would promise an action that
                cannot happen — and with the buttons gone, the colour of these
                two is itself the mark of a row that still needs somebody. */}
            {isPending(flag) && (
              <>
                <Tooltip
                  title={
                    publishesOnApprove(flag)
                      ? "The content is acceptable — publishes the picture"
                      : "The content is acceptable — resolves the report, publishes nothing"
                  }
                >
                  <span>
                    <Button
                      size="small"
                      color="success"
                      onClick={() => {
                        setReviewNotes("");
                        setConfirming({ flag, verdict: "approve" });
                      }}
                    >
                      Approve
                    </Button>
                  </span>
                </Tooltip>
                <Tooltip
                  title={
                    publishesOnApprove(flag)
                      ? "Not acceptable — it stays unpublished"
                      : canRemoveOnReject(flag)
                        ? "Upholds the report, and offers to take the picture down"
                        : "Upholds the report; nothing about the content changes"
                  }
                >
                  <span>
                    <Button
                      size="small"
                      color="error"
                      // Re-ticked every time the dialog opens. Carrying an untick
                      // from the previous row would leave a picture up that the
                      // moderator believed they had removed.
                      onClick={() => {
                        setRemovePicture(true);
                        setReviewNotes("");
                        setConfirming({ flag, verdict: "reject" });
                      }}
                    >
                      Reject
                    </Button>
                  </span>
                </Tooltip>
              </>
            )}
            {/* Always last within the group, so the verdicts keep the reading
                order and the menu never comes before them. */}
            <RowActionsMenu
              actions={secondary}
              ariaLabel={
                isPending(flag) ? "More actions" : "Actions for this flag"
              }
            />
          </Box>
        );
      },
    },
  ];

  return (
    <>
      {reviewError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {reviewError}
        </Alert>
      )}

      {reviewNextToken && (
        // The grid pages client-side, so it cannot fetch the rest. Saying so is
        // better than showing a partial queue that looks complete — but it is
        // information, not a warning, so it reads as a line of text rather than
        // a second coloured banner.
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          Showing the most recent {rows.length}. There are older {statusWord}
          flags than fit on this page.
        </Typography>
      )}

      {!loadingReviewFlags &&
        rows.length === 0 &&
        !reviewError &&
        !reviewNextToken && (
          // "Nothing waiting for review" is only true of the pending view.
          // Under Approved or Rejected an empty list is a statement about the
          // archive, and saying the queue is clear would be answering a
          // question the moderator did not ask.
          //
          // Not a green success banner: an empty queue is the ordinary state,
          // not an achievement, and colour here would outrank the verdict dots
          // that are the only thing in this view worth spotting.
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            {reviewStatus === "PENDING"
              ? "Nothing waiting for review."
              : `No ${statusWord}flags.`}
          </Typography>
        )}

      <FilterableDataGrid
        rows={rows}
        columns={columns}
        // Status is the only one of these that reaches the server. Kind and
        // Raised by narrow the rows already in memory; status is a query
        // parameter, and the rows for another status were never fetched.
        onFilterChange={(field, value) => {
          if (field === "status") {
            loadReviewFlags(value as ReviewStatusFilter);
          }
        }}
        filters={[
          {
            label: "Status",
            field: "status",
            // "All" has no request behind it — status is the partition key of
            // the index this reads — so the store fans out to one read per
            // status. It is also the one value the grid's own client-side
            // filter already understands as "do not narrow".
            //
            // Seeded from the store rather than a literal. The grid filters on
            // this field client-side too, so a default that disagreed with the
            // status actually fetched would filter every row it returned out
            // of the grid — a silently empty archive.
            defaultValue: reviewStatus,
            options: [
              { value: "ALL", label: "All" },
              { value: "PENDING", label: "Pending" },
              { value: "APPROVED", label: "Approved" },
              { value: "REJECTED", label: "Rejected" },
            ],
          },
          {
            label: "Kind",
            field: "contentType",
            options: [
              { value: "ALL", label: "All" },
              { value: "PROFILE_PICTURE", label: "Pictures" },
              { value: "USER", label: "Conduct reports" },
            ],
          },
          {
            label: "Raised by",
            field: "source",
            options: [
              { value: "ALL", label: "All" },
              { value: "REKOGNITION", label: "Automated" },
              { value: "MEMBER", label: "Member" },
              { value: "GUEST", label: "Guest" },
            ],
          },
        ]}
        searchFields={["senderId", "content", "id"]}
        searchPlaceholder="Search by member, content, or flag id..."
        height={600}
        pageSize={25}
        // The store caps this queue at 50 rows per (status, kind) — 100 for a
        // single status, 300 under ALL — and the grid pages at 25, so nothing
        // rendered here is large enough for virtualisation to earn its cost —
        // and with the resolution columns the row is wide enough that a
        // virtualised grid drops the action buttons out of the DOM.
        disableVirtualization
        // Decided rows are a record; pending rows are work. Under All the
        // record outnumbers the work, so the archive steps back a tone and the
        // eye lands on what still needs somebody. `text.secondary` rather than
        // `disabled`: still ~4.6:1 on white, so it recedes without becoming
        // hard to read.
        getRowClassName={({ row }) =>
          (row as ModerationQueueFlag).status === "PENDING"
            ? "row--pending"
            : "row--decided"
        }
        // Centre every cell's content down the row. The cells vary in height —
        // a 40px avatar, two-line cells, button clusters, plain text — so
        // without this they sit on different baselines and the row reads as
        // ragged.
        sx={{
          width: "100%",
          "& .MuiDataGrid-cell": {
            display: "flex",
            alignItems: "center",
          },
          // Pending rows are work; decided rows are a record. Under All the
          // record outnumbers the work and is usually newer, so the two have to
          // be told apart before anything is read — the separation is carried
          // by ground and tone together, not by one of them.
          //
          // `text.secondary` rather than `disabled`: ~5.7:1 on the tint, so it
          // steps back without becoming hard to read. The verdict dot and the
          // Approve/Reject buttons are the only saturated pixels either way, so
          // colour still means "act on this".
          // Rows that still need somebody read bold. The nested selectors are
          // not belt-and-braces: Typography and Button each set their own
          // font-weight from the theme, so a weight on the row alone would
          // cascade into almost nothing.
          "& .row--pending": {
            fontWeight: 700,
            "& .MuiTypography-root, & .MuiButton-root": {
              fontWeight: 700,
            },
          },
          "& .row--decided": {
            color: "text.secondary",
            backgroundColor: "grey.50",
          },
          "& .row--decided:hover": {
            backgroundColor: "grey.100",
          },
          // The picture on a settled row is evidence, not a thing to judge.
          "& .row--decided .MuiAvatar-root": {
            opacity: 0.55,
          },
          // Actions pinned to the right edge, so they stay reachable when the
          // grid is narrower than its columns. Done with sticky positioning
          // rather than `pinnedColumns`, which is a DataGrid Pro feature and
          // this project is on the community package.
          //
          // The backgrounds are not decoration: a sticky cell scrolls over the
          // ones beside it, so it has to carry its row's own ground or the
          // columns underneath show through it.
          '& .MuiDataGrid-cell[data-field="actions"]': {
            position: "sticky",
            right: 0,
            zIndex: 2,
            backgroundColor: "background.paper",
            borderLeft: "1px solid",
            borderLeftColor: "divider",
          },
          '& .MuiDataGrid-columnHeader[data-field="actions"]': {
            position: "sticky",
            right: 0,
            // Above the cells, which are themselves above the ordinary ones.
            zIndex: 3,
            backgroundColor: "background.paper",
            borderLeft: "1px solid",
            borderLeftColor: "divider",
          },
          '& .row--decided .MuiDataGrid-cell[data-field="actions"]': {
            backgroundColor: "grey.50",
          },
          '& .row--decided:hover .MuiDataGrid-cell[data-field="actions"]': {
            backgroundColor: "grey.100",
          },
          // MUI tints the row on hover with a translucent overlay, which a
          // sticky opaque cell would otherwise sit outside of.
          '& .MuiDataGrid-row:not(.row--decided):hover .MuiDataGrid-cell[data-field="actions"]':
            {
              backgroundColor: "action.hover",
            },
        }}
      />

      <ConfirmActionDialog
        open={confirming !== null}
        title={
          confirming?.verdict === "approve" ? "Approve this?" : "Reject this?"
        }
        message={
          confirming?.verdict === "approve" ? (
            confirming.flag && publishesOnApprove(confirming.flag) ? (
              <>
                The picture will be published to this member's profile. If they
                have published a different picture since this was flagged, the
                approval is refused rather than overwriting it.
              </>
            ) : (
              <>
                Records that a human found this acceptable. Nothing is published
                — there is no image behind this flag.
              </>
            )
          ) : confirming?.flag && canRemoveOnReject(confirming.flag) ? (
            <>
              This picture is live on the member&apos;s profile — upholding the
              report on its own does not change that.
            </>
          ) : (
            <>
              The content stays unpublished. Nothing is deleted: what was
              flagged is kept so the decision can be revisited.
            </>
          )
        }
        extraContent={
          <>
            {/* Out of `message`, which renders inside a role="alert" region: a
                screen reader announces an interactive control in there as part
                of the alert text, and this checkbox decides whether a member's
                picture comes down. Same reason the note field below is here. */}
            {confirming?.verdict === "reject" &&
              confirming.flag &&
              canRemoveOnReject(confirming.flag) && (
                <Box sx={{ mb: 2 }}>
                  <FormControlLabel
                    sx={{ display: "flex" }}
                    control={
                      <Checkbox
                        checked={removePicture}
                        onChange={(event) =>
                          setRemovePicture(event.target.checked)
                        }
                      />
                    }
                    label="Also remove the picture, and tell the member"
                  />
                  {removePicture ? (
                    <Typography variant="body2" color="text.secondary">
                      It comes off their profile, the directory and the in-call
                      roster. A copy is kept so the decision can be revisited,
                      and they are emailed. It does not stop them uploading
                      another — bar uploads as well if that is what you mean.
                    </Typography>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      The decision is recorded and the picture stays up. Leave
                      this unticked only if the picture is acceptable and the
                      report is not.
                    </Typography>
                  )}
                </Box>
              )}
            <TextField
              label="Note (optional)"
              value={reviewNotes}
              onChange={(event) => setReviewNotes(event.target.value)}
              size="small"
              fullWidth
              multiline
              minRows={2}
              // Said plainly, because the natural assumption is the opposite:
              // the member's email names the Terms section and the appeal route,
              // and never this. A moderator writing an explanation here for the
              // member would be writing it to nobody.
              helperText="Recorded on the flag for us. The member never sees it."
            />
          </>
        }
        confirmLabel={confirming?.verdict === "approve" ? "Approve" : "Reject"}
        onClose={() => setConfirming(null)}
        onConfirm={() =>
          confirming && decide(confirming.flag, confirming.verdict)
        }
      />

      <ConfirmActionDialog
        open={holding !== null}
        title="Place this picture under legal hold?"
        message={
          <>
            A copy of the picture is kept somewhere nothing can delete, and the
            flag is marked as held. Nothing is removed and the member is{" "}
            <strong>not told</strong> — not now, and not if you reject and
            remove the picture afterwards. Use this only for material you are
            escalating under the runbook. Only a developer can release a hold.
          </>
        }
        confirmLabel="Place hold"
        onClose={() => setHolding(null)}
        onConfirm={() => holding && hold(holding)}
      />

      <SuspendUploadsDialog
        userId={suspending?.senderId}
        onClose={() => setSuspending(null)}
        onDone={(message) => {
          setSuspending(null);
          showSnackbar(message, "success");
        }}
      />

      {SnackbarNode}
    </>
  );
};
